import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Layers, Send, Timer } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/v2/components/ui/popover";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/v2/components/ui/tooltip";
import { format, getISOWeek } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/v2/components/ui/button";
import { Card } from "@/v2/components/ui/card";
import { InfiniteCalendar } from "@/v2/components/InfiniteCalendar";
import { MonthPicker } from "@/shared/components/ui/monthpicker";
import { cn } from "@/v2/lib/utils";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { buildCalendarGrid, getWeekdayLabels } from "@/v2/lib/weekUtils";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type DaySums = {
	exportedBillable: number;
	exportedNonBillable: number;
	trackedBillable: number;
	trackedNonBillable: number;
};
export type CalendarViewMode = "all" | "tracked" | "exported";

export interface CalendarGridProps {
	dailySums?: Map<string, DaySums>;
}

export function emptyDaySums(): DaySums {
	return { exportedBillable: 0, exportedNonBillable: 0, trackedBillable: 0, trackedNonBillable: 0 };
}

/** Round to one decimal in a way that survives many additions cleanly. */
function round1(n: number): number {
	return Math.round(n * 10) / 10;
}

function roundedSums(d: DaySums): DaySums {
	return {
		exportedBillable: round1(d.exportedBillable),
		exportedNonBillable: round1(d.exportedNonBillable),
		trackedBillable: round1(d.trackedBillable),
		trackedNonBillable: round1(d.trackedNonBillable),
	};
}

function exportedTotal(d: DaySums): number {
	return d.exportedBillable + d.exportedNonBillable;
}

function trackedTotal(d: DaySums): number {
	return d.trackedBillable + d.trackedNonBillable;
}

/** Grand total across all 4 buckets. */
export function dayTotal(d: DaySums): number {
	return exportedTotal(d) + trackedTotal(d);
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function isSameDay(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isSameMonth(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function formatDateParam(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayKey(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isInRange(d: Date, from: Date | undefined, to: Date | undefined) {
	if (!from || !to) return false;
	return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
}

// ─────────────────────────────────────────────
// DayHoursGrid — inline numbers shown in cells / weekly column / strip / yearly
// ─────────────────────────────────────────────
//
// Layout (transposed, fixed skeleton):
//   - Columns: exported (left) | tracked (right). In "all" view both are always
//     rendered with a divider between them, even when one side is empty — keeps
//     positions identical cell-to-cell so the eye doesn't have to re-orient.
//     In single-bucket views ("exported" / "tracked"), only the chosen column.
//   - Rows: billable (top) | non-billable (bottom). Both rows always rendered.
//   - Zero values render blank inside their fixed cell (no "0.0h" filler).
// Visual cues (layered):
//   - Row position + opacity: billable = full opacity, non-billable = 60%.
//   - Column color: exported = text-foreground, tracked = text-muted-foreground.

interface DayHoursGridProps {
	sums: DaySums;
	viewMode: CalendarViewMode;
	/** When true, "billable" row uses heavier weight (used in weekly column for emphasis). */
	emphasizeBillable?: boolean;
}

function DayHoursGrid({ sums, viewMode, emphasizeBillable = false }: DayHoursGridProps) {
	const renderExportedCol = viewMode !== "tracked";
	const renderTrackedCol = viewMode !== "exported";

	// Suppress entirely if the visible buckets are all zero. This keeps days with
	// no relevant data from rendering an empty skeleton.
	const visibleSum =
		(renderExportedCol ? sums.exportedBillable + sums.exportedNonBillable : 0) +
		(renderTrackedCol ? sums.trackedBillable + sums.trackedNonBillable : 0);
	if (visibleSum <= 0) return null;

	const baseNumCls = "text-[10px] sm:text-xs leading-tight tabular-nums min-w-[2.5em] text-right";
	const billableWeightCls = emphasizeBillable ? "font-semibold" : "font-medium";

	// Use   (non-breaking space) for empty cells so each row keeps its
	// line-height. Without this, an empty non-billable row collapses to zero
	// height and the billable row slides down — making pure-billable days look
	// visually different from mixed-data days.
	const BLANK = " ";

	const nonBillableWeightCls = "font-normal opacity-60";
	const cellText = (n: number) => (n > 0 ? `${n.toFixed(1)}h` : BLANK);

	// Two-column case: render as a CSS grid with a single divider element that
	// spans both rows, so the bar never "splits" between rows the way per-row
	// border-l does.
	if (renderExportedCol && renderTrackedCol) {
		return (
			<div className="grid items-baseline grid-cols-[auto_1px_auto] gap-x-1.5 gap-y-0 sm:gap-y-0.5">
				<span className={cn("col-start-1 row-start-1", baseNumCls, billableWeightCls, "text-foreground")}>
					{cellText(sums.exportedBillable)}
				</span>
				<span className={cn("col-start-3 row-start-1", baseNumCls, billableWeightCls, "text-muted-foreground")}>
					{cellText(sums.trackedBillable)}
				</span>
				<span className={cn("col-start-1 row-start-2", baseNumCls, nonBillableWeightCls, "text-foreground")}>
					{cellText(sums.exportedNonBillable)}
				</span>
				<span className={cn("col-start-3 row-start-2", baseNumCls, nonBillableWeightCls, "text-muted-foreground")}>
					{cellText(sums.trackedNonBillable)}
				</span>
				<span aria-hidden className="col-start-2 row-start-1 row-span-2 self-stretch bg-border/60" />
			</div>
		);
	}

	// Single column — no divider needed.
	const isExportedOnly = renderExportedCol;
	const colorCls = isExportedOnly ? "text-foreground" : "text-muted-foreground";
	const bil = isExportedOnly ? sums.exportedBillable : sums.trackedBillable;
	const nb = isExportedOnly ? sums.exportedNonBillable : sums.trackedNonBillable;
	return (
		<div className="grid items-baseline grid-cols-1 gap-y-0 sm:gap-y-0.5">
			<span className={cn(baseNumCls, billableWeightCls, colorCls)}>{cellText(bil)}</span>
			<span className={cn(baseNumCls, nonBillableWeightCls, colorCls)}>{cellText(nb)}</span>
		</div>
	);
}

// ─────────────────────────────────────────────
// DaySumsTooltipContent — labeled grid tooltip
// ─────────────────────────────────────────────

function DaySumsTooltipContent({ sums }: { sums: DaySums }) {
	// Round each cell first, then derive row/column/grand totals from the *rounded*
	// display values so the tooltip stays self-consistent. (Summing raw values then
	// rounding can drift by 0.1h vs the visible numbers — e.g. 1.25 + 1.45 displays
	// as 1.3 + 1.5 but rounds raw to 2.7 instead of the visible 2.8.)
	const expBillable = round1(sums.exportedBillable);
	const expNonBillable = round1(sums.exportedNonBillable);
	const trBillable = round1(sums.trackedBillable);
	const trNonBillable = round1(sums.trackedNonBillable);

	const rowBillable = round1(expBillable + trBillable);
	const rowNonBillable = round1(expNonBillable + trNonBillable);
	const colExported = round1(expBillable + expNonBillable);
	const colTracked = round1(trBillable + trNonBillable);
	const grand = round1(rowBillable + rowNonBillable);

	const num = (n: number) => `${n.toFixed(1)}h`;

	return (
		<div className="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-0.5 text-xs tabular-nums">
			{/* Header row */}
			<span />
			<span className="text-right text-muted-foreground">exported</span>
			<span className="text-right text-muted-foreground">tracked</span>
			<span className="text-right text-muted-foreground">total</span>

			{/* Billable row */}
			<span className="text-muted-foreground">billable</span>
			<span className="text-right">{num(expBillable)}</span>
			<span className="text-right">{num(trBillable)}</span>
			<span className="text-right font-medium">{num(rowBillable)}</span>

			{/* Non-billable row */}
			<span className="text-muted-foreground">non-billable</span>
			<span className="text-right opacity-70">{num(expNonBillable)}</span>
			<span className="text-right opacity-70">{num(trNonBillable)}</span>
			<span className="text-right font-medium opacity-70">{num(rowNonBillable)}</span>

			{/* Divider */}
			<span className="col-span-4 border-t border-border/60 my-0.5" />

			{/* Totals row */}
			<span className="text-muted-foreground">total</span>
			<span className="text-right">{num(colExported)}</span>
			<span className="text-right">{num(colTracked)}</span>
			<span className="text-right font-semibold">{num(grand)}</span>
		</div>
	);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function Calendar({ dailySums = new Map() }: CalendarGridProps) {
	useDocumentTitle("Calendar");
	const { date: selectedDate, dateRange } = useDateParams();
	const hasRange = dateRange.from != null && dateRange.to != null;
	const navigate = useNavigate();
	const today = useMemo(() => new Date(), []);
	const [searchParams, setSearchParams] = useSearchParams();
	const viewMode = (searchParams.get("view") || "exported") as CalendarViewMode;
	const setViewMode = useCallback((mode: CalendarViewMode) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (mode === "exported") next.delete("view");
			else next.set("view", mode);
			return next;
		}, { replace: true });
	}, [setSearchParams]);

	const [viewedMonth, setViewedMonth] = useState(() => ({
		year: selectedDate.getFullYear(),
		month: selectedDate.getMonth(),
	}));

	// Sync viewed month when selected date or date range changes externally
	useEffect(() => {
		const anchor = hasRange && dateRange.from ? dateRange.from : selectedDate;
		setViewedMonth({ year: anchor.getFullYear(), month: anchor.getMonth() });
	}, [selectedDate, dateRange.from, dateRange.to, hasRange]);

	const { days: calendarDays, weeksNeeded } = useMemo(
		() => buildCalendarGrid(viewedMonth.year, viewedMonth.month),
		[viewedMonth.year, viewedMonth.month],
	);

	// Build weekly sums per row
	const weeklySums = useMemo(() => {
		const sums: DaySums[] = [];
		for (let week = 0; week < weeksNeeded; week++) {
			const acc = emptyDaySums();
			for (let d = 0; d < 7; d++) {
				const day = calendarDays[week * 7 + d];
				const ds = dailySums.get(dayKey(day));
				if (ds) {
					acc.exportedBillable += ds.exportedBillable;
					acc.exportedNonBillable += ds.exportedNonBillable;
					acc.trackedBillable += ds.trackedBillable;
					acc.trackedNonBillable += ds.trackedNonBillable;
				}
			}
			sums.push(roundedSums(acc));
		}
		return sums;
	}, [calendarDays, weeksNeeded, dailySums]);

	// Monthly sums for the viewed year (all 12 months)
	const monthlySums = useMemo(() => {
		const sums: DaySums[] = Array.from({ length: 12 }, () => emptyDaySums());
		dailySums.forEach((ds, key) => {
			const [yearStr, monthStr] = key.split("-");
			const year = parseInt(yearStr, 10);
			const month = parseInt(monthStr, 10) - 1;
			if (year === viewedMonth.year && month >= 0 && month < 12) {
				sums[month].exportedBillable += ds.exportedBillable;
				sums[month].exportedNonBillable += ds.exportedNonBillable;
				sums[month].trackedBillable += ds.trackedBillable;
				sums[month].trackedNonBillable += ds.trackedNonBillable;
			}
		});
		return sums.map(roundedSums);
	}, [dailySums, viewedMonth.year]);

	// Yearly total
	const yearlyTotal = useMemo(() => {
		const total = emptyDaySums();
		for (const ms of monthlySums) {
			total.exportedBillable += ms.exportedBillable;
			total.exportedNonBillable += ms.exportedNonBillable;
			total.trackedBillable += ms.trackedBillable;
			total.trackedNonBillable += ms.trackedNonBillable;
		}
		return roundedSums(total);
	}, [monthlySums]);

	// Auto-scroll the monthly strip to center the current month
	const monthStripRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!monthStripRef.current) return;
		const activeEl = monthStripRef.current.querySelector("[data-active=true]");
		if (activeEl) {
			activeEl.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
		}
	}, [viewedMonth.month, viewedMonth.year]);

	const goToPreviousMonth = useCallback(() => {
		setViewedMonth((prev) => {
			const d = new Date(prev.year, prev.month - 1, 1);
			return { year: d.getFullYear(), month: d.getMonth() };
		});
	}, []);

	const goToNextMonth = useCallback(() => {
		setViewedMonth((prev) => {
			const d = new Date(prev.year, prev.month + 1, 1);
			return { year: d.getFullYear(), month: d.getMonth() };
		});
	}, []);

	const goToToday = useCallback(() => {
		setViewedMonth({ year: today.getFullYear(), month: today.getMonth() });
	}, [today]);

	const onSelectDate = (d: Date) => {
		if (isSameDay(d, today)) {
			navigate("/entries");
		} else {
			navigate(`/entries?date=${formatDateParam(d)}`);
		}
	};

	const viewedMonthDate = new Date(viewedMonth.year, viewedMonth.month, 1);
	const isMobile = useIsMobile();

	// Mobile: infinite scroll calendar
	if (isMobile) {
		return (
			<InfiniteCalendar
				dailySums={dailySums}
				viewMode={viewMode}
				onViewModeChange={setViewMode}
				selectedDate={selectedDate}
				dateRange={dateRange}
			/>
		);
	}

	// Desktop: grid calendar
	return (
		<div className="flex h-full flex-col p-3 sm:p-6">
			{/* Action bar */}
			<div className="flex items-center gap-2 mb-3">
				<h1 className="text-4xl font-semibold tracking-tight text-muted-foreground">Calendar</h1>
				<div className="ml-auto">
					<Tabs value={viewMode} onValueChange={(v) => setViewMode(v as CalendarViewMode)}>
						<TabsList className="h-7 sm:h-8">
							<TabsTrigger value="exported" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Send className="size-3" />Exported
							</TabsTrigger>
							<TabsTrigger value="tracked" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Timer className="size-3" />Tracked
							</TabsTrigger>
							<TabsTrigger value="all" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Layers className="size-3" />All
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			</div>

			{/* Monthly totals strip + yearly total */}
			<Card className="flex mb-3 overflow-hidden">
				<div className="flex-1 flex overflow-x-auto scrollbar-hide" ref={monthStripRef}>
					{monthlySums.map((ms, i) => {
						const isActive = i === viewedMonth.month;
						const msExp = round1(exportedTotal(ms));
						const msTr = round1(trackedTotal(ms));
						const hasData = msExp > 0 || msTr > 0;
						return (
							<button
								key={i}
								data-active={isActive}
								onClick={() => setViewedMonth((prev) => ({ ...prev, month: i }))}
								className={cn(
									"flex-1 min-w-[60px] sm:min-w-[80px] flex flex-col items-start px-2 py-2 sm:px-3 sm:py-2.5 transition-colors hover:bg-muted/50 rounded-md",
								)}
							>
								<span className={cn(
									"text-[10px] sm:text-xs font-medium",
									isActive ? "text-primary" : "text-muted-foreground",
								)}>
									<span className="sm:hidden">{format(new Date(viewedMonth.year, i, 1), "MMM")}</span>
									<span className="hidden sm:inline">{format(new Date(viewedMonth.year, i, 1), "MMMM")}</span>
								</span>
								<div className="flex flex-col items-start mt-0.5">
									{viewMode !== "tracked" && (
										<span className={cn(
											"text-[10px] sm:text-xs font-semibold tabular-nums min-w-[2.5em]",
											!hasData && "text-muted-foreground/40 font-normal",
										)}>
											{msExp.toFixed(1)}h
										</span>
									)}
									{viewMode !== "exported" && (
										<span className={cn(
											"text-[10px] sm:text-xs text-muted-foreground tabular-nums min-w-[2.5em]",
											!hasData && "text-muted-foreground/40",
										)}>
											{msTr.toFixed(1)}h
										</span>
									)}
								</div>
							</button>
						);
					})}
				</div>

				{/* Yearly total */}
				<div className="shrink-0 flex flex-col items-start px-3 py-2 sm:px-5 sm:py-2.5 border-l-2 border-border ml-2 min-w-[6rem] sm:min-w-[9rem]">
					<span className="text-[10px] sm:text-xs font-medium text-muted-foreground">
						{viewedMonth.year === today.getFullYear() ? `${viewedMonth.year} Year to Date` : `${viewedMonth.year}`}
					</span>
					<div className="flex flex-col items-start mt-0.5">
						{viewMode !== "tracked" && (
							<span className="text-[10px] sm:text-xs font-semibold tabular-nums min-w-[2.5em]">
								{round1(exportedTotal(yearlyTotal)).toFixed(1)}h
							</span>
						)}
						{viewMode !== "exported" && (
							<span className="text-[10px] sm:text-xs text-muted-foreground tabular-nums min-w-[2.5em]">
								{round1(trackedTotal(yearlyTotal)).toFixed(1)}h
							</span>
						)}
					</div>
				</div>
			</Card>

			<Card className="flex flex-1 flex-col overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-1 px-3 py-2 sm:gap-2 sm:px-4 sm:py-3 border-b border-border bg-neutral-100 dark:bg-transparent">
					<div className="flex items-center">
						<Button variant="ghost" size="icon" onClick={goToPreviousMonth} className="h-7 w-7 text-muted-foreground sm:h-8 sm:w-8">
							<ChevronLeft className="size-3.5 sm:size-4" />
						</Button>
						<Button variant="ghost" size="icon" onClick={goToNextMonth} className="h-7 w-7 text-muted-foreground sm:h-8 sm:w-8">
							<ChevronRight className="size-3.5 sm:size-4" />
						</Button>
					</div>

					<Popover>
						<PopoverTrigger asChild>
							<button className="inline-flex items-center gap-1 text-sm font-medium sm:text-xl sm:font-semibold hover:text-foreground/80 transition-colors">
								{format(viewedMonthDate, "MMMM yyyy")}
								<ChevronDown className="size-3 sm:size-4 text-muted-foreground" />
							</button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-auto p-0">
							<MonthPicker
								selectedMonth={viewedMonthDate}
								onMonthSelect={(d) => setViewedMonth({ year: d.getFullYear(), month: d.getMonth() })}
							/>
						</PopoverContent>
					</Popover>

					<Button
						variant="ghost"
						size="sm"
						onClick={goToToday}
						className="ml-auto h-7 gap-1 text-[11px] sm:h-8 sm:gap-1.5 sm:text-sm"
					>
						<CalendarDays className="size-3 sm:size-4" />
						Today
					</Button>
				</div>

				{/* Grid */}
				<div
					className="grid flex-1"
					style={{
						gridTemplateColumns: "repeat(7, 1fr) auto",
						gridTemplateRows: `auto repeat(${weeksNeeded}, 1fr)`,
					}}
				>
					{/* Weekday headers */}
					{(() => {
						const fullLabels = getWeekdayLabels("full");
						const letterLabels = getWeekdayLabels("letter");
						return fullLabels.map((day, i) => (
							<div
								key={day}
								className={cn(
									"border-b border-border py-2 text-xs text-muted-foreground sm:py-2.5 sm:text-[13px] bg-neutral-100 dark:bg-transparent",
									"text-center sm:text-left sm:pl-3",
									i > 0 && "border-l border-l-border",
								)}
							>
								<span className="sm:hidden">{letterLabels[i]}</span>
								<span className="hidden sm:inline">{day}</span>
							</div>
						));
					})()}
					{/* Week column header */}
					<div className="border-b border-border border-l-2 border-l-border py-2 text-center text-xs text-muted-foreground sm:py-2.5 sm:text-[13px] bg-neutral-100 dark:bg-transparent sm:px-3">
						<span className="sm:hidden">Wk</span>
						<span className="hidden sm:inline">Week</span>
					</div>

					{/* Rows */}
					{Array.from({ length: weeksNeeded }).map((_, weekIdx) => {
						const weekDays = calendarDays.slice(weekIdx * 7, weekIdx * 7 + 7);
						const weekNum = getISOWeek(weekDays[3]);
						const ws = weeklySums[weekIdx];

						return (
							<React.Fragment key={weekIdx}>
								{weekDays.map((currDay, col) => {
									const isCurrMonth = isSameMonth(currDay, viewedMonthDate);
									const isDayToday = isSameDay(currDay, today);
									const isSelected = !hasRange && isSameDay(currDay, selectedDate);
									const inRange = hasRange && isInRange(currDay, dateRange.from, dateRange.to);
									const rangeStart = hasRange && dateRange.from && isSameDay(currDay, dateRange.from);
									const rangeEnd = hasRange && dateRange.to && isSameDay(currDay, dateRange.to);
									const isFuture = currDay.getTime() > today.getTime() && !isDayToday;
									// Show month prefix at boundaries: when adjacent day is a different month
								const globalIdx = weekIdx * 7 + col;
								const prevDay = globalIdx > 0 ? calendarDays[globalIdx - 1] : null;
								const nextDay = globalIdx < calendarDays.length - 1 ? calendarDays[globalIdx + 1] : null;
								const showMonthPrefix =
									(prevDay && prevDay.getMonth() !== currDay.getMonth()) ||
									(nextDay && nextDay.getMonth() !== currDay.getMonth());
									const hasCircle = isDayToday || isSelected;

									const ds = dailySums.get(dayKey(currDay));
									const hasAnyData = ds && dayTotal(ds) > 0;

									return (
										<button
											key={`${weekIdx}-${col}`}
											onClick={() => onSelectDate(currDay)}
											className={cn(
												"flex flex-col border-t border-border p-1 text-left transition-colors bg-background hover:bg-muted/50 sm:p-2",
												col > 0 && "border-l border-l-border",
												isDayToday && "ring-[1.5px] ring-inset ring-highlight",
												isSelected && !isDayToday && "ring-[1.5px] ring-inset ring-primary",
												inRange && "bg-primary/5",
												rangeStart && "bg-primary/8",
												rangeEnd && "bg-primary/8",
											)}
										>
											{/* Opacity wrapper so muting cascades over the hours grid too — text-* on
											    inner spans would otherwise override an inherited muted color. */}
											<div
												className={cn(
													"flex flex-1 flex-col w-full",
													!isCurrMonth && "opacity-40",
													isFuture && isCurrMonth && "opacity-60",
												)}
											>
												<div className="flex items-center gap-1">
													{showMonthPrefix && (
														<span className="text-[10px] sm:text-xs leading-none text-muted-foreground">
															{format(currDay, "MMM")}
														</span>
													)}
													{hasCircle ? (
														<span
															className={cn(
																"flex h-5 w-5 items-center justify-center rounded-full text-xs sm:h-6 sm:w-6 sm:text-sm",
																isDayToday && "bg-highlight text-white",
																isSelected && !isDayToday && "bg-primary text-primary-foreground",
															)}
														>
															{currDay.getDate()}
														</span>
													) : (
														<span className="text-xs sm:text-sm leading-none text-muted-foreground">
															{currDay.getDate()}
														</span>
													)}
												</div>
												{hasAnyData && ds && (
													<TooltipProvider delayDuration={200}>
														<Tooltip>
															<TooltipTrigger asChild>
																<div className="mt-auto w-fit p-0.5 rounded">
																	<DayHoursGrid sums={ds} viewMode={viewMode} />
																</div>
															</TooltipTrigger>
															<TooltipContent side="top" align="center" className="px-3 py-2">
																<DaySumsTooltipContent sums={ds} />
															</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												)}
											</div>
										</button>
									);
								})}

								{/* Week cell */}
								<button
									key={`week-${weekIdx}`}
									onClick={() => {
										const from = weekDays[0];
										const to = weekDays[6];
										navigate(`/entries?from=${formatDateParam(from)}&to=${formatDateParam(to)}`);
									}}
									className="flex flex-col items-center justify-between border-t border-border border-l-2 border-l-border bg-muted/20 p-1 sm:p-2 hover:bg-muted/40 transition-colors cursor-pointer"
								>
									<span className="text-[9px] sm:text-[10px] font-medium text-muted-foreground/60">
										W{weekNum}
									</span>
									{dayTotal(ws) > 0 && (
										<TooltipProvider delayDuration={200}>
											<Tooltip>
												<TooltipTrigger asChild>
													<div className="mt-auto px-1 p-0.5 rounded w-fit">
														<DayHoursGrid sums={ws} viewMode={viewMode} emphasizeBillable />
													</div>
												</TooltipTrigger>
												<TooltipContent side="left" align="center" className="px-3 py-2">
													<DaySumsTooltipContent sums={ws} />
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</button>
							</React.Fragment>
						);
					})}
				</div>
			</Card>
		</div>
	);
}
